import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';

import {
  activeCasesApi,
  adminEventsApi,
  adminUsersApi,
  aircraftStatusesApi,
  alertsApi,
  caseTimelineApi,
  caseTrackingApi,
  domainEventsApi,
  geoLibraryApi,
  hubsResourceReportApi,
  mapTimelineApi,
  newCasesApi,
  nopResourcesApi,
  settingsApi,
  simpleStatisticsApi,
  statsApi,
  surgeonsApi,
  weatherSnapshotApi,
} from '#entities';

import { rtkQueryErrorLogger } from '#shared/api';

import { persistConfig } from './persistConfig';
import { rootReducer } from './rootReducer';
import { listenForUiSessionWrites } from './uiSessionCrossTab';

const persistedReducer = persistReducer(persistConfig, rootReducer);

// Kept wired with zero listeners registered: `registerMigrationListeners` was
// the only consumer and went away with the layout system (TMDXNC-45). The
// middleware itself is inert until something calls `startListening`.
export const listenerMiddleware = createListenerMiddleware();

export const initStore = () => {
  const store = configureStore({
    reducer: persistedReducer,
    middleware: getDefault =>
      getDefault({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
        immutableCheck: {
          warnAfter: 128,
        },
      })
        .prepend(listenerMiddleware.middleware)
        .concat(
          newCasesApi.middleware,
          hubsResourceReportApi.middleware,
          nopResourcesApi.middleware,
          geoLibraryApi.middleware,
          adminUsersApi.middleware,
          adminEventsApi.middleware,
          activeCasesApi.middleware,
          caseTimelineApi.middleware,
          caseTrackingApi.middleware,
          aircraftStatusesApi.middleware,
          surgeonsApi.middleware,
          simpleStatisticsApi.middleware,
          statsApi.middleware,
          weatherSnapshotApi.middleware,
          mapTimelineApi.middleware,
          domainEventsApi.middleware,
          alertsApi.middleware,
          settingsApi.middleware,
          // SpecRef: ARCH-005 — global capture of RTK Query failures to Sentry
          rtkQueryErrorLogger,
        ),
    devTools:
      import.meta.env.REACT_APP_ENV === 'local' ||
      import.meta.env.REACT_APP_ENV === 'development',
  });

  // TMDXNC-45: the widgetManager slice and its nested persister are gone; its
  // localStorage entry is unreachable by the root migration, so clear it once.
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('persist:widgetManager');
  }

  const persistor = persistStore(store);
  listenForUiSessionWrites(store.dispatch);

  return { store, persistor };
};

export type AppStore = ReturnType<typeof initStore>['store'];
export type AppDispatch = AppStore['dispatch'];
